/**
 * CRM Navigation Bridge
 * 
 * When embedded in GHL iframe: sends postMessage to parent for seamless SPA navigation.
 * When standalone: falls back to window.open.
 * 
 * The parent bridge script dynamically detects the GHL location ID from the URL,
 * so no hardcoded IDs needed. Works across all locations.
 */

const isEmbedded = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

const sendToParent = (data) => {
  window.parent.postMessage({ source: 'optsc-app', ...data }, '*');
};

export const crmNavigate = {
  /**
   * Open the INTERNAL Patient Profile page for a contact.
   * This is the single source of truth for "open contact details" across all
   * modules (Leads, Scheduling, Inbox, Call Tracker). Always uses the internal
   * Patient Management module — never redirects to external GHL.
   *
   * @param {Function} navigate — react-router navigate function
   * @param {string} contactId — GHL contactId
   * @param {object} opts — { section: 'overview'|'general'|'appointments'|... }
   */
  openPatientProfile: (navigate, contactId, opts = {}) => {
    if (!contactId) return;
    const section = opts.section ? `#${opts.section}` : '';
    navigate(`/patients/${contactId}${section}`);
  },

  /**
   * @deprecated Use openPatientProfile instead. Kept for backwards compat with
   * legacy code paths that still expect to bounce out to GHL. New code should
   * always call openPatientProfile.
   *
   * Open contact profile — parent script handles location ID dynamically
   */
  openContact: (contactId, { crmBaseUrl, ghlLocationId } = {}) => {
    if (isEmbedded()) {
      sendToParent({ action: 'open-contact', contactId });
    } else if (crmBaseUrl && ghlLocationId && contactId) {
      window.open(`${crmBaseUrl}/v2/location/${ghlLocationId}/contacts/detail/${contactId}`, 'crm-window');
    }
  },

  /**
   * Open inbox conversation — parent script handles location ID dynamically
   */
  openInbox: (contactId, conversationId, { crmBaseUrl, ghlLocationId, contactName } = {}) => {
    if (isEmbedded()) {
      sendToParent({ action: 'open-inbox', contactId, conversationId, contactName });
    } else if (crmBaseUrl && ghlLocationId) {
      const path = conversationId
        ? `${crmBaseUrl}/v2/location/${ghlLocationId}/conversations/conversations/${conversationId}?category=team-inbox&tab=all`
        : `${crmBaseUrl}/v2/location/${ghlLocationId}/conversations/conversations/?category=team-inbox&tab=all`;
      window.open(path, 'crm-window');
    }
  },

  /**
   * Open external URL (Juvonno, etc.) — always new window
   */
  openExternal: (url, target = 'external-window') => {
    if (!url) return;
    if (isEmbedded()) {
      sendToParent({ action: 'open-external', url, target });
    } else {
      window.open(url, target);
    }
  },

  /**
   * Open Juvonno patient profile
   */
  openJuvonno: (juvonnoPortalUrl, customerId) => {
    if (!juvonnoPortalUrl || !customerId) return;
    const url = `${juvonnoPortalUrl}/patient/profile.php?txtCustomerID=${customerId}`;
    crmNavigate.openExternal(url, 'juvonno-window');
  },

  /**
   * Navigate to any path within GHL SPA
   */
  navigateTo: (path, fallbackUrl) => {
    if (isEmbedded()) {
      sendToParent({ action: 'navigate', path });
    } else if (fallbackUrl) {
      window.open(fallbackUrl, 'crm-window');
    }
  },

  isEmbedded,
};
